/**
 * ardySearchSuggestions — Client-side Ardy suggestion engine for search.
 * Derives up to 3 actionable suggestions from audit data.
 * Pure function, zero API calls.
 */

import { relativeTime } from './relativeTime';
import type { VerdictSummary } from '@/types';

export interface ArdySuggestion {
  icon: string;
  label: string;
  title: string;
  detail: string;
  itemId: string;
}

const DAY_MS = 86400000;

export function getArdySearchSuggestions(
  summaries: VerdictSummary[],
  floorHourly: number,
): ArdySuggestion[] {
  const suggestions: ArdySuggestion[] = [];

  // 1. Most underpriced: active piece with largest negative hourly gap
  const underpriced = summaries
    .filter(p => !p.sold && p.effectiveHourly != null && p.effectiveHourly < floorHourly)
    .sort((a, b) => (a.effectiveHourly ?? 0) - (b.effectiveHourly ?? 0));

  if (underpriced.length > 0) {
    const p = underpriced[0];
    const gapDollars = Math.round(Math.abs((p.effectiveHourly ?? 0) - floorHourly) / 100);
    suggestions.push({
      icon: '\u2193',
      label: 'Most underpriced',
      title: p.title || `${p.width}\u00D7${p.height}"`,
      detail: `$${gapDollars}/hr below floor`,
      itemId: p.id,
    });
  }

  // 2. Top earner: highest effective hourly among non-sold pieces
  const earners = summaries
    .filter(p => !p.sold && p.effectiveHourly != null)
    .sort((a, b) => (b.effectiveHourly ?? 0) - (a.effectiveHourly ?? 0));

  if (earners.length > 0) {
    const top = earners[0];
    const hourlyDollars = Math.round((top.effectiveHourly ?? 0) / 100);
    suggestions.push({
      icon: '\u2605',
      label: 'Top earner',
      title: top.title || `${top.width}\u00D7${top.height}"`,
      detail: `$${hourlyDollars}/hr`,
      itemId: top.id,
    });
  }

  // 3. Needs re-audit: oldest active piece not audited in >14 days
  const stale = summaries
    .filter(p => !p.sold)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  if (stale.length > 0) {
    const oldest = stale[0];
    const daysOld = Math.floor((Date.now() - new Date(oldest.createdAt).getTime()) / DAY_MS);
    if (daysOld > 14) {
      suggestions.push({
        icon: '\u25F7',
        label: 'Needs re-audit',
        title: oldest.title || `${oldest.width}\u00D7${oldest.height}"`,
        detail: `Last priced ${relativeTime(oldest.createdAt)}`,
        itemId: oldest.id,
      });
    }
  }

  return suggestions;
}
